// -- Dialog ---------------------------------------------------
// Wires [data-dialog-trigger] buttons to <dialog> elements, plus the
// named-state API so agents/tests can drive states by name (AGENTS.md "State API").

// Shared preamble (AGENTS.md "State API"); the implementation lives in core.js —
// build.ts rewrites this import into a df$.shadcn.shared binding in dist/.
import { defussGlobals } from '../../shared/state-api.js';

const df$ = defussGlobals();

const dialogStates = ['default', 'open'];

/**
 * UI side of setState: 'default' closes, 'open' opens modally. Native
 * <dialog> can't animate to a declared state it's not in, so this is a
 * direct showModal()/close() dispatch; unknown names are rejected upstream.
 */
function triggerStateChange(dialog, stateName, _config) {
  switch (stateName) {
    case 'default':
      if (dialog.open) dialog.close();
      break;
    case 'open':
      if (!dialog.open) dialog.showModal();
      break;
  }
}

/** Registry-level API; pass the dialog element explicitly. Unknown names throw. */
export const dialogApi = {
  setState(dialog, stateName, config = {}) {
    if (!dialogStates.includes(stateName)) {
      throw new Error(`dialog: unknown state "${stateName}" (supported: ${dialogStates.join(', ')})`);
    }
    triggerStateChange(dialog, stateName, config);
    // state lives on the ELEMENT, not the module (multiple dialogs per page)
    dialog.dataset.stateName = stateName;
    dialog._stateConfig = config;
  },
  getState(dialog) {
    return { name: dialog.dataset.stateName || 'default', config: dialog._stateConfig ?? {} };
  },
};

df$.dialogApi = dialogApi;
df$.dialogStates = dialogStates;

function init() {
  document.querySelectorAll('[data-dialog-trigger]:not([data-init])').forEach((trigger) => {
    trigger.dataset.init = '';
    const dialog = document.getElementById(trigger.dataset.dialogTrigger);
    if (!dialog) return;
    trigger.addEventListener('click', () => {
      dialog._trigger = trigger;
      dialog.showModal();
    });
  });
  /* .command excluded: the command component owns its dialogs (own backdrop
     close, filtering, focus). Without this, dialog.js - which loads first —
     claims them via data-init and command.js's init silently skips them. */
  document.querySelectorAll('dialog:not(.alert-dialog):not(.sheet):not(.command):not(.window):not(.cookie-consent-dialog):not([data-init])').forEach((dialog) => {
    dialog.dataset.init = '';
    // bind-scope the api per instance: `$('#confirm').api.setState('open')`
    dialog.api = {
      setState: (stateName, config) => dialogApi.setState(dialog, stateName, config),
      getState: () => dialogApi.getState(dialog),
    };
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
    dialog.querySelectorAll('[data-dialog-close]').forEach((btn) => {
      btn.addEventListener('click', () => { dialog.close(); });
    });
    dialog.addEventListener('close', () => {
      // `close` fires AFTER the exit transition (display allow-discrete), so a
      // fast re-open can beat it - a stale event must not downgrade an open
      // dialog back to 'default' or yank focus out of it while it's showing.
      if (dialog.open) return;
      // reflect the actual UI state: any close path (Escape, backdrop, close
      // button) returns the dialog to 'default', even when it wasn't setState'd
      dialog.dataset.stateName = 'default';
      if (dialog._trigger) dialog._trigger.focus();
    });
  });
}

init();
new MutationObserver(init).observe(document, { childList: true, subtree: true });
